import React from 'react';
import {
  Box,
  Container,
  Heading,
  Text,
  Button,
  VStack,
  HStack,
  SimpleGrid,
  Badge,
  Flex,
  Link,
  Center,
} from '@chakra-ui/react';
import { Link as RouterLink } from 'react-router-dom';

const MarketingPage: React.FC = () => {
  const features = [
    {
      title: 'Location-Based Intelligence',
      description: 'AI-powered suggestions tailored to your specific city and neighborhood',
      icon: '📍',
    },
    {
      title: 'Weather-Aware Recommendations',
      description: 'Smart filtering based on real-time weather conditions',
      icon: '🌤️',
    },
    {
      title: 'Budget-Conscious Planning',
      description: 'Activities from free to premium, matching your spending preferences',
      icon: '💰',
    },
    {
      title: 'Flexible Time Options',
      description: 'From 30-minute quick activities to full-day adventures',
      icon: '⏰',
    },
  ];

  const steps = [
    {
      number: '01',
      title: 'Enter Your Preferences',
      description: 'Tell us your location, budget, available time, and what kind of vibe you\'re feeling',
    },
    {
      number: '02',
      title: 'Spin the Wheel',
      description: 'Watch as our AI generates personalized activity suggestions based on your inputs',
    },
    {
      number: '03',
      title: 'Discover & Explore',
      description: 'Get your unique suggestion and start your spontaneous adventure immediately',
    },
  ];

  return (
    <Box minH="100vh" bg="white" color="purple.900">
      {/* Navigation */}
      <Box as="nav" py={6} px={8} position="fixed" top={0} left={0} right={0} zIndex={1000} bg="white" backdropFilter="blur(10px)" borderBottom="1px solid" borderColor="purple.200">
        <Container maxW="7xl">
          <Flex justify="space-between" align="center">
            <Box>
              <Heading size="xl" color="purple.500" fontWeight="bold">
                IDK
              </Heading>
                  <Text fontSize="sm" color="purple.600" mt={-1}>
                    I Don't Know
                  </Text>
            </Box>
            <HStack gap={8}>
              <RouterLink to="/support">
                <Link color="purple.600" _hover={{ color: 'purple.500' }} fontWeight="medium">
                  Support
                </Link>
              </RouterLink>
              <Button
                bg="purple.500"
                color="white"
                _hover={{ bg: 'purple.600' }}
                px={6}
                py={3}
                borderRadius="full"
                fontWeight="semibold"
                cursor="not-allowed"
                opacity={0.6}
              >
                Coming Soon
              </Button>
            </HStack>
          </Flex>
        </Container>
      </Box>

      {/* Hero Section */}
      <Box pt={32} pb={20} bg="white">
        <Container maxW="7xl">
          <VStack gap={12} textAlign="center">
            <VStack gap={6}>
              <Badge
                bg="purple.50"
                color="purple.600"
                px={4}
                py={2}
                borderRadius="full"
                fontSize="sm"
                fontWeight="semibold"
                letterSpacing="wide"
                border="1px solid"
                borderColor="purple.200"
              >
                🎰 THE SLOT MACHINE FOR ADVENTURE
              </Badge>
              
              <Heading
                as="h1"
                size="4xl"
                fontWeight="black"
                lineHeight="shorter"
                maxW="4xl"
                color="purple.900"
              >
                When you don't know what to do,{' '}
                <Text as="span" color="purple.500">
                  spin your way to adventure
                </Text>
              </Heading>
              
              <Text
                fontSize="xl"
                color="purple.900"
                maxW="2xl"
                lineHeight="tall"
                fontWeight="medium"
              >
                Get AI-powered activity suggestions tailored to your location, budget, and mood. 
                Perfect for spontaneous explorers and routine-breakers.
              </Text>
            </VStack>

            <HStack gap={4} pt={4}>
              <Button
                size="lg"
                bg="black"
                color="white"
                _hover={{ bg: 'gray.800' }}
                px={8}
                py={6}
                fontSize="lg"
                fontWeight="bold"
                borderRadius="xl"
                shadow="lg"
                transition="all 0.3s ease"
                cursor="not-allowed"
                opacity={0.6}
              >
                iOS - Coming Soon
              </Button>
              <Button
                size="lg"
                variant="outline"
                borderColor="purple.300"
                color="purple.900"
                _hover={{ 
                  borderColor: 'purple.500', 
                  color: 'purple.500',
                  bg: 'purple.50'
                }}
                px={8}
                py={6}
                fontSize="lg"
                fontWeight="semibold"
                borderRadius="xl"
                transition="all 0.3s ease"
                cursor="not-allowed"
              >
                Android - Coming Soon
              </Button>
            </HStack>
          </VStack>
        </Container>
      </Box>

      {/* App Preview Section */}
      <Box py={20} bg="gray.50">
        <Container maxW="7xl">
          <VStack gap={16}>
            <VStack gap={4} textAlign="center">
              <Heading size="2xl" fontWeight="bold" color="gray.900">
                Experience the Magic
              </Heading>
              <Text fontSize="lg" color="purple.900" maxW="2xl">
                Watch how IDK transforms "I don't know what to do" into your next great adventure
              </Text>
            </VStack>
            
            <Center>
              <Box
                w="380px"
                h="520px"
                bg="white"
                borderRadius="3xl"
                border="2px solid"
                borderColor="purple.200"
                p={0}
                position="relative"
                shadow="xl"
                overflow="hidden"
              >
                {/* Header Section */}
                <Box
                  w="full"
                  h="180px"
                  bgGradient="linear(to-br, purple.400, purple.600)"
                  display="flex"
                  alignItems="center"
                  justifyContent="center"
                  position="relative"
                >
                  <VStack gap={3}>
                    <Box
                      w="60px"
                      h="60px"
                      bg="white"
                      borderRadius="full"
                      display="flex"
                      alignItems="center"
                      justifyContent="center"
                      shadow="lg"
                    >
                      <Text fontSize="2xl">🎰</Text>
                    </Box>
                    <Text fontSize="xl" fontWeight="bold" color="white">IDK</Text>
                  </VStack>
                </Box>
                
                {/* Content Section */}
                <VStack gap={6} p={8} align="start">
                  <VStack gap={2} align="start" w="full">
                    <Text fontSize="2xl" fontWeight="bold" color="purple.900">Perfect for:</Text>
                    <Box w="full" h="1px" bg="purple.200" />
                  </VStack>
                  
                  <VStack gap={4} align="start" w="full">
                    {[
                      { icon: '📍', text: 'Location-based suggestions' },
                      { icon: '🌤️', text: 'Weather-aware activities' },
                      { icon: '💰', text: 'Budget-friendly options' },
                      { icon: '⏰', text: 'Time-flexible planning' }
                    ].map((item, index) => (
                      <HStack key={index} gap={4} align="center" w="full">
                        <Box
                          w="40px"
                          h="40px"
                          bg="purple.100"
                          borderRadius="lg"
                          display="flex"
                          alignItems="center"
                          justifyContent="center"
                        >
                          <Text fontSize="lg">{item.icon}</Text>
                        </Box>
                        <Text fontSize="md" color="purple.700" fontWeight="medium" flex={1}>
                          {item.text}
                        </Text>
                      </HStack>
                    ))}
                  </VStack>
                  
                  <Box w="full" mt={4}>
                    <Button
                      w="full"
                      bg="purple.500"
                      color="white"
                      _hover={{ bg: 'purple.600' }}
                      py={4}
                      borderRadius="xl"
                      fontWeight="bold"
                      fontSize="md"
                      cursor="not-allowed"
                      opacity={0.7}
                    >
                      Coming Soon
                    </Button>
                  </Box>
                </VStack>
              </Box>
            </Center>
          </VStack>
        </Container>
      </Box>

      {/* Features Section */}
      <Box py={20} bg="white">
        <Container maxW="7xl">
          <VStack gap={16}>
            <VStack gap={6} textAlign="center">
              <Heading size="2xl" fontWeight="bold" color="gray.900">
                Why Choose IDK?
              </Heading>
              <Text fontSize="lg" color="purple.900" maxW="2xl">
                Advanced AI technology meets spontaneous adventure planning
              </Text>
            </VStack>

            <SimpleGrid columns={{ base: 1, md: 2, lg: 4 }} gap={8}>
              {features.map((feature, index) => (
                <Box
                  key={index}
                  p={8}
                  bg="gray.50"
                  borderRadius="2xl"
                  border="1px solid"
                  borderColor="gray.200"
                  textAlign="center"
                  _hover={{
                    borderColor: 'purple.500',
                    transform: 'translateY(-8px)',
                    shadow: 'xl',
                    transition: 'all 0.3s ease',
                  }}
                >
                  <VStack gap={6}>
                    <Text fontSize="4xl">{feature.icon}</Text>
                    <VStack gap={3}>
                      <Heading size="md" color="gray.900">
                        {feature.title}
                      </Heading>
                      <Text color="purple.900" fontSize="sm" lineHeight="tall">
                        {feature.description}
                      </Text>
                    </VStack>
                  </VStack>
                </Box>
              ))}
            </SimpleGrid>
          </VStack>
        </Container>
      </Box>

      {/* How It Works Section */}
      <Box py={20} bg="gray.50">
        <Container maxW="7xl">
          <VStack gap={16}>
            <VStack gap={6} textAlign="center">
              <Heading size="2xl" fontWeight="bold" color="gray.900">
                How It Works
              </Heading>
              <Text fontSize="lg" color="purple.900">
                Three simple steps to your next adventure
              </Text>
            </VStack>

            <SimpleGrid columns={{ base: 1, md: 3 }} gap={12} maxW="5xl">
              {steps.map((step, index) => (
                <VStack key={index} gap={6} textAlign="center">
                  <Box
                    w={20}
                    h={20}
                    bg="purple.500"
                    borderRadius="full"
                    display="flex"
                    alignItems="center"
                    justifyContent="center"
                    fontSize="2xl"
                    fontWeight="black"
                    color="white"
                    shadow="lg"
                  >
                    {step.number}
                  </Box>
                  <VStack gap={4}>
                    <Heading size="lg" color="gray.900">
                      {step.title}
                    </Heading>
                    <Text color="purple.900" fontSize="md" lineHeight="tall">
                      {step.description}
                    </Text>
                  </VStack>
                </VStack>
              ))}
            </SimpleGrid>
          </VStack>
        </Container>
      </Box>

      {/* Pricing Section */}
      <Box py={20} bg="white">
        <Container maxW="7xl">
          <VStack gap={16}>
            <VStack gap={6} textAlign="center">
              <Heading size="2xl" fontWeight="bold" color="gray.900">
                Simple, Transparent Pricing
              </Heading>
              <Text fontSize="lg" color="purple.900">
                Start free, upgrade when you want unlimited adventures
              </Text>
            </VStack>

            <SimpleGrid columns={{ base: 1, md: 2 }} gap={8} maxW="3xl">
              {/* Free Plan */}
              <Box
                p={10}
                bg="gray.50"
                borderRadius="2xl"
                border="1px solid"
                borderColor="gray.200"
                textAlign="center"
                position="relative"
              >
                <VStack gap={8}>
                  <VStack gap={2}>
                    <Heading size="xl" color="gray.900">Free</Heading>
                    <Text fontSize="sm" color="purple.900">Perfect for trying out IDK</Text>
                  </VStack>
                  
                  <VStack gap={2}>
                    <Text fontSize="4xl" fontWeight="black" color="purple.500">
                      $0
                    </Text>
                    <Text fontSize="sm" color="purple.900">forever</Text>
                  </VStack>
                  
                  <VStack gap={4} align="start" w="full">
                    <HStack gap={3}>
                      <Text color="purple.500" fontSize="lg">✓</Text>
                      <Text fontSize="sm" color="purple.900">2 spins per month</Text>
                    </HStack>
                    <HStack gap={3}>
                      <Text color="purple.500" fontSize="lg">✓</Text>
                      <Text fontSize="sm" color="purple.900">Location-based suggestions</Text>
                    </HStack>
                    <HStack gap={3}>
                      <Text color="purple.500" fontSize="lg">✓</Text>
                      <Text fontSize="sm" color="purple.900">Weather-aware recommendations</Text>
                    </HStack>
                    <HStack gap={3}>
                      <Text color="purple.500" fontSize="lg">✓</Text>
                      <Text fontSize="sm" color="purple.900">Budget and time filtering</Text>
                    </HStack>
                  </VStack>
                </VStack>
              </Box>

              {/* Premium Plan */}
              <Box
                p={10}
                bg="white"
                borderRadius="2xl"
                border="2px solid"
                borderColor="purple.500"
                textAlign="center"
                position="relative"
                shadow="xl"
              >
                <Badge
                  position="absolute"
                  top={-4}
                  left="50%"
                  transform="translateX(-50%)"
                  bg="purple.500"
                  color="white"
                  px={4}
                  py={2}
                  borderRadius="full"
                  fontSize="sm"
                  fontWeight="bold"
                >
                  Most Popular
                </Badge>
                
                <VStack gap={8}>
                  <VStack gap={2}>
                    <Heading size="xl" color="gray.900">Premium</Heading>
                    <Text fontSize="sm" color="purple.900">For unlimited adventures</Text>
                  </VStack>
                  
                  <VStack gap={2}>
                    <HStack align="baseline" gap={2}>
                      <Text fontSize="4xl" fontWeight="black" color="purple.500">
                        $1.99
                      </Text>
                      <Text fontSize="lg" color="purple.900">/month</Text>
                    </HStack>
                    <Text fontSize="sm" color="purple.900">or $21.49/year (save 10%)</Text>
                  </VStack>
                  
                  <VStack gap={4} align="start" w="full">
                    <HStack gap={3}>
                      <Text color="purple.500" fontSize="lg">✓</Text>
                      <Text fontSize="sm" color="purple.900">Unlimited spins</Text>
                    </HStack>
                    <HStack gap={3}>
                      <Text color="purple.500" fontSize="lg">✓</Text>
                      <Text fontSize="sm" color="purple.900">Save last 5 activities</Text>
                    </HStack>
                    <HStack gap={3}>
                      <Text color="purple.500" fontSize="lg">✓</Text>
                      <Text fontSize="sm" color="purple.900">Share activities with friends</Text>
                    </HStack>
                    <HStack gap={3}>
                      <Text color="purple.500" fontSize="lg">✓</Text>
                      <Text fontSize="sm" color="purple.900">Priority support</Text>
                    </HStack>
                    <HStack gap={3}>
                      <Text color="purple.500" fontSize="lg">✓</Text>
                      <Text fontSize="sm" color="purple.900">Advanced personalization</Text>
                    </HStack>
                  </VStack>
                  
                  <Button
                    w="full"
                    bg="gray.100"
                    color="purple.900"
                    _hover={{ bg: 'gray.200' }}
                    py={6}
                    borderRadius="xl"
                    fontWeight="bold"
                    fontSize="lg"
                    cursor="not-allowed"
                  >
                    Coming Soon
                  </Button>
                </VStack>
              </Box>
            </SimpleGrid>
          </VStack>
        </Container>
      </Box>

      {/* CTA Section */}
      <Box py={20} bg="gray.50">
        <Container maxW="7xl">
          <Box
            p={16}
            bg="white"
            borderRadius="3xl"
            border="1px solid"
            borderColor="gray.200"
            textAlign="center"
            position="relative"
            overflow="hidden"
          >
            <Box
              position="absolute"
              top={0}
              left={0}
              right={0}
              h="4px"
              bgGradient="linear(to-r, purple.500, brand.400, purple.500)"
            />
            
            <VStack gap={8}>
              <VStack gap={4}>
                <Heading size="2xl" fontWeight="bold" color="gray.900">
                  Ready to Break Out of Your Routine?
                </Heading>
                <Text fontSize="lg" color="purple.900" maxW="2xl" lineHeight="tall">
                  Join thousands of adventurers who will use IDK to discover amazing activities 
                  and create unforgettable memories in their city.
                </Text>
              </VStack>
              
              <Button
                size="lg"
                bg="gray.100"
                color="purple.900"
                _hover={{ bg: 'gray.200' }}
                px={12}
                py={8}
                fontSize="xl"
                fontWeight="bold"
                borderRadius="2xl"
                shadow="2xl"
                transition="all 0.3s ease"
                cursor="not-allowed"
              >
                Coming Soon - Stay Tuned!
              </Button>
              
              <Text fontSize="sm" color="gray.500">
                iOS & Android versions in development
              </Text>
            </VStack>
          </Box>
        </Container>
      </Box>

      {/* Footer */}
      <Box as="footer" py={16} borderTop="1px solid" borderColor="gray.200" bg="white">
        <Container maxW="7xl">
          <VStack gap={12}>
            <Box w="full" h="1px" bg="gray.200" />
            
            <Flex justify="space-between" align="center" wrap="wrap" w="full">
              <Box>
                <Heading size="lg" color="purple.500" fontWeight="bold">
                  IDK
                </Heading>
                <Text fontSize="sm" color="purple.900" mt={-1}>
                  I Don't Know • The Slot Machine for Adventure
                </Text>
              </Box>
              
              <HStack gap={8}>
                <RouterLink to="/support">
                  <Link color="purple.900" _hover={{ color: 'purple.500' }} fontSize="sm">
                    Support
                  </Link>
                </RouterLink>
                <RouterLink to="/privacy">
                  <Link color="purple.900" _hover={{ color: 'purple.500' }} fontSize="sm">
                    Privacy Policy
                  </Link>
                </RouterLink>
                <Link href="/terms" color="purple.900" _hover={{ color: 'purple.500' }} fontSize="sm">
                  Terms of Service
                </Link>
              </HStack>
            </Flex>
            
            <Text color="gray.500" fontSize="sm" textAlign="center">
              © 2025 IDK App. All rights reserved. Made with ❤️ for spontaneous adventurers.
            </Text>
          </VStack>
        </Container>
      </Box>
    </Box>
  );
};

export default MarketingPage;